/*
 * Copyright (c) 2025.
 * This code and its derivatives belong to BETICA LTD and may not be copied, reproduced, amended or used in any way without the permission of BETICA LTD
 * @author torrejonv
 *
 */

/**
 * @fileoverview Redux slice for managing sports tree data within the application.
 * Provides functionality to fetch, store, and manage sports tree information.
 * @module redux/slices/sportsTreeSlice
 */

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import createAxiosInstance from "../async/axios";
import { getRequestParams } from "../async/get-fetch-params";
import { getAuthTimezoneOffset } from "../reselect/auth-selector";

/**
 * Creates the initial state for the sports tree slice
 *
 * @function getInitialState
 * @param {Object} [sportsTreeCache={}] - Initial cache for secondary auxiliary searches
 * @param {Object|null} [sportsTreeData=null] - Initial sports tree data
 * @returns {Object} The initial state object with the structure:
 *   - error: null|string - Error message if API request fails
 *   - loading: boolean - Loading state indicator
 *   - sportsTreeCache: object - Cache for secondary searches
 *   - sportsTreeData: object|null - Primary sports tree data
 */
export const getInitialState = (sportsTreeCache = {}, sportsTreeData = null) => ({
  error: null,
  loading: false,
  sportsTreeCache, // cache for secondary aux searches
  sportsTreeData,
});

/**
 * Utility function that converts a number to a two-digit string with leading zeros if needed
 *
 * @function to2Digits
 * @param {number} number - The number to convert
 * @returns {string} A string representation of the number with at least 2 digits
 */
function to2Digits(number) {
  return String(number).padStart(2, "0");
}

/**
 * Generates a timezone string in the format "+HH:MM" or "-HH:MM"
 * Based on either the provided offset or the local browser timezone
 *
 * @function getTimezone
 * @param {number} [authTimezoneOffset] - Timezone offset in hours from authentication state
 * @returns {string} Formatted timezone string in format "+HH:MM" or "-HH:MM"
 */
function getTimezone(authTimezoneOffset) {
  if (authTimezoneOffset) {
    const timezoneHeader = `${Math.sign(authTimezoneOffset) > 0 ? "+" : ""}${to2Digits(authTimezoneOffset)}:00`;

    return timezoneHeader;
  }
  const offset = new Date().getTimezoneOffset() * -1;
  const offsetHours = offset / 60;
  const offsetMinutes = Math.abs(offset % 60);
  const timezoneHeader = `${Math.sign(offsetHours) > 0 ? "+" : ""}${to2Digits(offsetHours)}:${to2Digits(
    offsetMinutes,
  )}`;

  return timezoneHeader;
}

/**
 * Async thunk that fetches sports tree data from the API
 *
 * @function getSportsTree
 * @async
 * @param {Object} params - Parameters for the request
 * @param {string} [params.cacheKey] - Key to store the result in cache
 * @param {Object} params.cancelToken - Axios cancel token for request cancellation
 * @param {string} [params.fromDate] - Start date for filtering sports events (YYYY-MM-DD format)
 * @param {boolean} params.slipstreamOn - Flag to determine endpoint path construction
 * @param {boolean} params.standard - If true, uses "ccoupon" endpoint, otherwise "acoupon"
 * @param {string} [params.toDate] - End date for filtering sports events (YYYY-MM-DD format)
 * @param {Object} thunkAPI - The Redux Toolkit thunk API object
 * @returns {Promise<Object>} Object containing cache key and sports tree data
 * @throws {Object} Custom error object with message, name and status properties
 */
export const getSportsTree = createAsyncThunk(
  "sportsTree/standardSportsTree",
  async ({ cacheKey, cancelToken, fromDate, slipstreamOn, standard, toDate }, thunkAPI) => {
    try {
      const { accountId, authToken, language, lineId, originId } = getRequestParams(thunkAPI.getState());
      const authTimezoneOffset = getAuthTimezoneOffset(thunkAPI.getState());

      const sportsTreeType = standard ? "ccoupon" : "acoupon";

      let optionalParams = "";
      if (fromDate) {
        optionalParams = `${optionalParams}&fromDate=${fromDate}`;
      }
      if (toDate) {
        optionalParams = `${optionalParams}&toDate=${toDate}`;
      }

      const axios = createAxiosInstance(thunkAPI.dispatch, {
        authToken,
        language,
      });
      axios.defaults.headers["timezone"] = getTimezone(authTimezoneOffset);

      const result = await axios.get(
        `/player/${
          !slipstreamOn && accountId ? `acc/${accountId}/` : ""
        }ept/${sportsTreeType}?originId=${originId}&lineId=${lineId}${optionalParams}`,
        { cancelToken },
      );

      return {
        cacheKey,
        sportsTreeData: result.data,
      };
    } catch (err) {
      const customError = {
        message: err.response?.headers["x-information"] || "Unable to obtain the sports tree", // serializable (err.response.data)
        name: "Sports Tree Error",
        status: err.response?.statusText,
      };
      throw customError;
    }
  },
);

/**
 * @constant sportsTreeSlice
 * @description Redux slice for sportsTree management
 */
const sportsTreeSlice = createSlice({
  /**
   * Extra reducers to handle the async thunk actions
   * @param {import('@reduxjs/toolkit').ActionReducerMapBuilder} builder - The builder for configuring reducers
   */
  extraReducers: (builder) => {
    builder
      /**
       * Sets loading state when sports tree request starts
       */
      .addCase(getSportsTree.pending, (state) => {
        state.error = null;
        state.loading = true;
      })
      /**
       * Handles errors when sports tree request fails
       */
      .addCase(getSportsTree.rejected, (state, action) => {
        state.error = action.error.message;
        state.loading = false;
      })
      /**
       * Stores sports tree data when request succeeds
       * If cacheKey is provided, stores in cache; otherwise updates main data
       */
      .addCase(getSportsTree.fulfilled, (state, action) => {
        state.error = null;
        state.loading = false;
        if (action.payload.cacheKey) {
          state.sportsTreeCache[action.payload.cacheKey] = action.payload.sportsTreeData;
        } else {
          state.sportsTreeData = action.payload.sportsTreeData;
        }
      });
  },
  initialState: getInitialState(),
  name: "sportsTree",
  reducers: {},
});
const { reducer } = sportsTreeSlice;

/**
 * The sports tree reducer function to be used in the Redux store
 * @type {import('@reduxjs/toolkit').Reducer}
 */
export default reducer;
